import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NAV } from "@/constants/testIds";

const links = [
  { to: "/catalog", label: "Catalog", tid: NAV.catalog },
  { to: "/authors", label: "Authors", tid: NAV.authors },
  { to: "/journal", label: "Journal", tid: NAV.journal },
  { to: "/about", label: "Manifesto", tid: NAV.about },
];

export default function Nav() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-md bg-[#050505]/75 border-b vv-line"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid={NAV.logo} className="group">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_story-stage-18/artifacts/ygm2ivyo_ac177a3c-e752-4d60-986d-4a6b6608ec1e.png"
              alt="A. Lee Creations"
              className="h-9 md:h-10 w-9 md:w-10 object-cover"
            />
            <div className="flex items-baseline gap-2">
              <span className="font-serif-vv text-xl md:text-2xl italic leading-none">
                A. Lee
              </span>
              <span className="font-serif-vv text-xl md:text-2xl leading-none">
                Creations
              </span>
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.tid}
              className={({ isActive }) =>
                `text-xs uppercase tracking-[0.22em] vv-link ${
                  isActive
                    ? "text-[color:var(--vv-bone)]"
                    : "text-[color:var(--vv-bone-dim)] hover:text-[color:var(--vv-bone)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/admin"
            data-testid={NAV.admin}
            className="text-xs uppercase tracking-[0.22em] text-[color:var(--vv-bone-dim)] hover:text-[color:var(--vv-ember)] vv-link"
          >
            Editor
          </Link>
        </nav>

        <button
          className="md:hidden text-[color:var(--vv-bone)] text-xs uppercase tracking-widest"
          data-testid={NAV.mobileToggle}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "Close" : "Menu"}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.19, 1, 0.22, 1] }}
            className="md:hidden overflow-hidden border-t vv-line bg-[#050505]/95 backdrop-blur"
          >
            <div className="px-6 py-8 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`${l.tid}-mobile`}
                  className="font-serif-vv text-3xl"
                >
                  {l.label}
                </NavLink>
              ))}
              <NavLink
                to="/admin"
                data-testid="nav-admin-mobile"
                className="font-serif-vv text-3xl text-[color:var(--vv-ember)]"
              >
                Editor
              </NavLink>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
